'use client';

import Link from 'next/link';

import { formatTickerToURLFriendly } from '@benzinga/utils';

import { TickerLogo } from '../../../../edge/dashboard/_components/TickerLogo';
import { widgetRowHoverClassName } from '../../../../edge/dashboard/_components/widgetChrome';
import { PORTFOLIO_TABLE_CELL_CLASS, PORTFOLIO_TABLE_NUMERIC_CLASS } from '../FinancialTable/watchlistQuotesTableChrome';
import { PricedHolding } from '../portfolioActionUtils';
import { formatHoldingCurrency, formatSignedCurrency, hasKnownHoldingGain } from '../portfolioFormatters';

interface HoldingRowProps {
  logo?: string | null;
  row: PricedHolding;
  totalValue: number;
}

const COST_BASIS_UNAVAILABLE = 'Cost basis unavailable';

const gainClassName = (hasKnownGain: boolean, gain: number, base = ''): string => {
  if (!hasKnownGain) return `${base} text-slate-400`.trim();
  return `${base} ${gain >= 0 ? 'text-bzgreen-500' : 'text-bzred-500'}`.trim();
};

const HoldingSymbolCell = ({ logo, row }: { logo?: string | null; row: PricedHolding }) => (
  <Link
    className="group flex min-w-0 items-center gap-2"
    href={`/quote/${formatTickerToURLFriendly(row.symbol)}`}
  >
    <TickerLogo size={20} src={logo} symbol={row.symbol} />
    <span className="min-w-0">
      <span className="block truncate font-extrabold leading-5 text-bzblue-900 group-hover:underline">{row.symbol}</span>
      {row.companyName ? (
        <span className="mt-0.5 block truncate text-xs leading-4 text-slate-400">{row.companyName}</span>
      ) : null}
    </span>
  </Link>
);

export const HoldingsMobileRow = ({ logo, row }: { logo?: string | null; row: PricedHolding }) => {
  const hasPrice = row.price > 0;
  const gain = row.marketValue - row.costBasis;
  const hasKnownGain = hasKnownHoldingGain(hasPrice, row.costBasis, gain);

  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
      <div className="min-w-0 flex-1">
        <HoldingSymbolCell logo={logo} row={row} />
        <p className="mt-0.5 truncate text-xs leading-tight text-slate-500">{row.quantity.toFixed(2)} shares</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-bold tabular-nums leading-tight text-slate-900">
          {hasPrice ? formatHoldingCurrency(row.marketValue) : '—'}
        </p>
        <p
          className={gainClassName(hasKnownGain, gain, 'mt-0.5 text-xs font-semibold tabular-nums leading-tight')}
          title={hasPrice && !hasKnownGain ? COST_BASIS_UNAVAILABLE : undefined}
        >
          {hasKnownGain ? formatSignedCurrency(gain) : '—'}
        </p>
      </div>
    </div>
  );
};

export const HoldingsDesktopRow = ({ logo, row, totalValue }: HoldingRowProps) => {
  const hasPrice = row.price > 0;
  const gain = row.marketValue - row.costBasis;
  const hasKnownGain = hasKnownHoldingGain(hasPrice, row.costBasis, gain);

  return (
    <tr className={`border-b border-slate-100 last:border-0 ${widgetRowHoverClassName}`}>
      <td className={PORTFOLIO_TABLE_CELL_CLASS}>
        <HoldingSymbolCell logo={logo} row={row} />
      </td>
      <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right tabular-nums text-slate-700`}>
        {row.quantity.toFixed(2)}
      </td>
      <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right tabular-nums text-slate-700`}>
        {hasPrice ? formatHoldingCurrency(row.price) : '—'}
      </td>
      <td
        className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right font-semibold tabular-nums text-slate-900`}
      >
        {hasPrice ? formatHoldingCurrency(row.marketValue) : '—'}
      </td>
      <td
        className={gainClassName(
          hasKnownGain,
          gain,
          `${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right font-semibold tabular-nums`,
        )}
        title={hasPrice && !hasKnownGain ? COST_BASIS_UNAVAILABLE : undefined}
      >
        {hasKnownGain ? formatSignedCurrency(gain) : '—'}
      </td>
      <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right tabular-nums text-slate-700`}>
        {hasPrice && totalValue > 0 ? `${((row.marketValue / totalValue) * 100).toFixed(1)}%` : '—'}
      </td>
    </tr>
  );
};
